import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  makeApi,
  composeAddress,
  type AdminCustomerSummary,
  type IssuePriority,
} from '../api/client';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_BYTES = 10 * 1024 * 1024;

interface LinkOption {
  projectId: string;
  addressId: string;
  label: string;
}

type Step = 'search' | 'form';

export function AdminNewIssuePage(): JSX.Element {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('search');

  // Customer autocomplete state
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<AdminCustomerSummary[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<AdminCustomerSummary | null>(null);
  const [linkOptions, setLinkOptions] = useState<LinkOption[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const comboboxRef = useRef<HTMLDivElement>(null);

  // Issue form state
  const [linkKey, setLinkKey] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<IssuePriority>('medium');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitLabel, setSubmitLabel] = useState('Create issue');
  const [error, setError] = useState<string | null>(null);

  // Debounced autocomplete lookup on the email field.
  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults([]); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      api.adminListCustomers({ search: q })
        .then((r) => { if (!cancelled) setResults(r.customers); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, api]);

  // Close the dropdown on outside click.
  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (comboboxRef.current && !comboboxRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  async function selectCustomer(c: AdminCustomerSummary): Promise<void> {
    setError(null);
    setSelectedCustomer(c);
    setDropdownOpen(false);
    setQuery('');
    setResults([]);
    setLoadingLinks(true);
    try {
      const detail = await api.adminGetCustomer(c.sub);
      const opts: LinkOption[] = [];
      for (const link of detail.links) {
        const addr = detail.addresses.find((a) => a.addressId === link.addressId);
        const proj = detail.projects.find((p) => p.pid === link.projectId);
        if (!addr || !proj) continue;
        opts.push({
          projectId: link.projectId,
          addressId: link.addressId,
          label: `${composeAddress(addr)} — ${proj.projectReferenceNumber}`,
        });
      }
      setLinkOptions(opts);
      if (opts.length > 0) setLinkKey(`${opts[0].projectId}|${opts[0].addressId}`);
      else setLinkKey('');
      setStep('form');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load customer details');
      setSelectedCustomer(null);
    } finally {
      setLoadingLinks(false);
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    const valid: File[] = [];
    for (const f of files) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        setError(`${f.name}: only image files (JPEG, PNG, WebP, GIF) are allowed`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        setError(`${f.name}: exceeds 10 MB limit`);
        continue;
      }
      valid.push(f);
    }
    setPendingFiles((prev) => [...prev, ...valid]);
  }

  function removeFile(idx: number): void {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  // One stable object URL per File reference. Keyed on File identity (not
  // index) so typing in the description textarea doesn't churn the cache and
  // doesn't re-trigger createObjectURL (which would otherwise reload the img).
  const previewUrlCacheRef = useRef<Map<File, string>>(new Map());
  const previewUrls = pendingFiles.map((f) => {
    const cache = previewUrlCacheRef.current;
    let url = cache.get(f);
    if (!url) {
      url = URL.createObjectURL(f);
      cache.set(f, url);
    }
    return url;
  });
  useEffect(() => {
    const cache = previewUrlCacheRef.current;
    const live = new Set(pendingFiles);
    for (const [f, url] of cache) {
      if (!live.has(f)) {
        URL.revokeObjectURL(url);
        cache.delete(f);
      }
    }
  }, [pendingFiles]);
  useEffect(() => {
    const cache = previewUrlCacheRef.current;
    return () => {
      cache.forEach((u) => URL.revokeObjectURL(u));
      cache.clear();
    };
  }, []);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!selectedCustomer || !linkKey) return;
    setError(null);
    const [projectId, addressId] = linkKey.split('|');
    setSubmitting(true);
    try {
      setSubmitLabel('Creating issue…');
      const issue = await api.adminCreateIssue({
        customerSub: selectedCustomer.sub,
        projectId,
        addressId,
        title: title.trim(),
        description: description.trim(),
        priority,
      });

      for (let i = 0; i < pendingFiles.length; i++) {
        const file = pendingFiles[i];
        setSubmitLabel(`Uploading file ${i + 1} of ${pendingFiles.length}…`);
        try {
          const { uploadUrl } = await api.adminRequestAttachmentUpload(issue.issueId, {
            projectId,
            addressId,
            filename: file.name,
            contentType: file.type,
            size: file.size,
          });
          await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
          });
        } catch {
          setError(`Issue created but failed to upload ${file.name}. You can add it from the issue page.`);
        }
      }

      navigate(
        `/admin/issues/${issue.issueId}?projectId=${encodeURIComponent(projectId)}&addressId=${encodeURIComponent(addressId)}`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create issue');
      setSubmitLabel('Create issue');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/admin/issues')}
          className="text-gray-500 hover:text-gray-300 transition-colors text-sm"
        >
          ← Back to issues
        </button>
      </div>

      <h1 className="text-2xl font-semibold text-white">Create issue on customer's behalf</h1>

      {/* Step 1: customer autocomplete */}
      <div className="card space-y-4">
        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
          Step 1 — Select customer
        </h2>

        {selectedCustomer ? (
          <div className="flex items-center justify-between bg-gray-800 rounded-md px-4 py-3">
            <div>
              <div className="text-sm text-white font-medium">{selectedCustomer.email}</div>
              <div className="text-xs text-gray-500">{selectedCustomer.accountStatus}</div>
            </div>
            <button
              type="button"
              className="text-xs text-gray-400 hover:text-white"
              onClick={() => {
                setSelectedCustomer(null);
                setLinkOptions([]);
                setLinkKey('');
                setStep('search');
                setError(null);
              }}
            >
              Change
            </button>
          </div>
        ) : (
          <div className="relative" ref={comboboxRef}>
            <label className="label">Customer email</label>
            <input
              className="input w-full"
              type="search"
              autoComplete="off"
              placeholder="Start typing an email…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setDropdownOpen(true); }}
              onFocus={() => setDropdownOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setDropdownOpen(false); }
                if (e.key === 'Enter' && !searching && results[0]) {
                  e.preventDefault();
                  selectCustomer(results[0]);
                }
              }}
              disabled={loadingLinks}
            />
            {dropdownOpen && query.trim() && (
              <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-700 bg-gray-900 shadow-lg max-h-64 overflow-y-auto">
                {searching && (
                  <div className="p-3 text-xs text-gray-500">Searching…</div>
                )}
                {!searching && results.length === 0 && (
                  <div className="p-3 text-xs text-gray-500">No matches</div>
                )}
                {!searching && results.map((c) => (
                  <button
                    key={c.sub}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectCustomer(c)}
                    disabled={loadingLinks}
                    className="w-full text-left px-3 py-2 hover:bg-gray-800/60 transition-colors border-b border-gray-800 last:border-0"
                  >
                    <div className="text-sm text-white">{c.email}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {c.accountStatus}
                      {c.accountTier === 'advanced' && ' · advanced'}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 2: issue form */}
      {step === 'form' && selectedCustomer && (
        <div className="card space-y-4">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
            Step 2 — Issue details
          </h2>

          {linkOptions.length === 0 ? (
            <div className="alert-amber">
              This customer has no linked project/address pairs. Link them to a project first.
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="label">Property / project</label>
                <select
                  className="input"
                  value={linkKey}
                  onChange={(e) => setLinkKey(e.target.value)}
                  required
                >
                  {linkOptions.map((o) => (
                    <option
                      key={`${o.projectId}|${o.addressId}`}
                      value={`${o.projectId}|${o.addressId}`}
                    >
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Title</label>
                <input
                  className="input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                  required
                />
              </div>

              <div>
                <label className="label">Description</label>
                <textarea
                  className="input min-h-[140px]"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={10_000}
                />
                {pendingFiles.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {pendingFiles.map((f, i) => (
                      <div
                        key={i}
                        className="relative group border border-gray-700 rounded overflow-hidden bg-gray-900"
                      >
                        <img
                          src={previewUrls[i]}
                          alt={f.name}
                          className="h-20 w-20 object-cover"
                        />
                        <button
                          type="button"
                          aria-label={`Remove ${f.name}`}
                          className="absolute top-0 right-0 bg-black/70 text-white text-xs leading-none px-1.5 py-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                          onClick={() => removeFile(i)}
                          disabled={submitting}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={submitting}
                  >
                    Add image
                  </button>
                  <span className="ml-2 text-xs text-gray-500">
                    Images only, up to 10 MB each.
                  </span>
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    multiple
                    accept={ALLOWED_TYPES.join(',')}
                    onChange={onFileChange}
                  />
                </div>
              </div>

              <div>
                <label className="label">Priority</label>
                <select
                  className="input"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as IssuePriority)}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              {error && <div className="text-sm text-red-400">{error}</div>}

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting || !title.trim() || !linkKey}
                >
                  {submitting ? submitLabel : 'Create issue'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate('/admin/issues')}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {error && step === 'search' && <div className="text-sm text-red-400">{error}</div>}
    </div>
  );
}
